// src/pages/auth/ForgotPassword.tsx
// Multi-step forgot password workflow matching Dualon design aesthetics and backend APIs.
import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  ShieldCheck,
  RefreshCw,
  Check,
} from 'lucide-react'
import toast from '@/utils/toast'
import { useAuth } from '@/app/hooks'
import {
  useForgotPasswordInitiateMutation,
  useForgotPasswordVerifyOtpMutation,
  useForgotPasswordResetMutation,
} from '@/features/auth/auth.api'
import {
  initiateSchema,
  verifyOtpSchema,
  resetPasswordSchema,
  sanitizeEmail,
  sanitizeOtp,
  sanitizePassword,
  type InitiateFormValues,
  type VerifyOtpFormValues,
  type ResetPasswordFormValues,
} from '@/features/auth/forgotPassword.schema'
import Logo from '../../assets/logo2.png'
import loginImage from '../../assets/login_img1.png'

type Step = 'initiate' | 'verify' | 'reset' | 'success'

interface ApiErrorResponse {
  data?: {
    message?: string
    error?: {
      code?: string
      message?: string
      traceId?: string
    }
    success?: boolean
  }
}

export default function ForgotPassword() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const [step, setStep] = useState<Step>('initiate')
  const [email, setEmail] = useState<string>('')
  const [resetToken, setResetToken] = useState<string>('')
  const [countdown, setCountdown] = useState<number>(60)
  const [canResend, setCanResend] = useState<boolean>(false)

  const [showNewPassword, setShowNewPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)

  // API Mutations
  const [initiateOtp, { isLoading: isInitiating }] = useForgotPasswordInitiateMutation()
  const [verifyOtp, { isLoading: isVerifying }] = useForgotPasswordVerifyOtpMutation()
  const [resetPassword, { isLoading: isResetting }] = useForgotPasswordResetMutation()

  // Step 1: Initiate Form
  const initiateForm = useForm<InitiateFormValues>({
    resolver: zodResolver(initiateSchema),
    mode: 'onTouched',
    defaultValues: { email: '' },
  })

  // Step 2: Verify OTP Form
  const verifyForm = useForm<VerifyOtpFormValues>({
    resolver: zodResolver(verifyOtpSchema),
    mode: 'onTouched',
    defaultValues: { otpCode: '' },
  })

  // 6 separate digits ref & state for sleek OTP inputs
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', ''])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Step 3: Reset Password Form
  const resetForm = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onTouched',
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  // Countdown timer for OTP Resend
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (step === 'verify' && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [step, countdown])

  // Sync OTP digits with React Hook Form
  const updateOtpDigits = (newDigits: string[]) => {
    setOtpDigits(newDigits)
    const combined = newDigits.join('')
    verifyForm.setValue('otpCode', combined, { shouldValidate: combined.length === 6 })
  }

  // Handle individual digit input change
  const handleDigitChange = (index: number, val: string) => {
    const sanitized = sanitizeOtp(val)
    if (!sanitized) {
      const nextDigits = [...otpDigits]
      nextDigits[index] = ''
      updateOtpDigits(nextDigits)
      return
    }

    // If pasted or typed multiple digits into one box
    if (sanitized.length > 1) {
      const chars = sanitized.split('').slice(0, 6)
      const nextDigits = [...otpDigits]
      chars.forEach((c, i) => {
        if (index + i < 6) nextDigits[index + i] = c
      })
      updateOtpDigits(nextDigits)
      const targetFocusIndex = Math.min(index + chars.length, 5)
      inputRefs.current[targetFocusIndex]?.focus()
      return
    }

    const nextDigits = [...otpDigits]
    nextDigits[index] = sanitized
    updateOtpDigits(nextDigits)

    // Automatically focus next digit box
    if (sanitized && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  // Handle keyboard backspace and arrows in OTP input
  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otpDigits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  // Handle paste full OTP
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = sanitizeOtp(e.clipboardData.getData('text'))
    if (!pasted) return

    const chars = pasted.split('').slice(0, 6)
    const nextDigits = ['', '', '', '', '', '']
    chars.forEach((c, idx) => {
      nextDigits[idx] = c
    })
    updateOtpDigits(nextDigits)
    const focusTarget = Math.min(chars.length, 5)
    inputRefs.current[focusTarget]?.focus()
  }

  // Redirect if authenticated
  if (isAuthenticated) return <Navigate to="/dashboard" replace />

  // ────────────────────────────────────────────────────────────────────────────
  // Step 1: Submit Email for OTP
  // ────────────────────────────────────────────────────────────────────────────
  const onInitiateSubmit = async (values: InitiateFormValues) => {
    try {
      toast.dismiss('forgot-password-initiate')
      const targetEmail = sanitizeEmail(values.email)
      const res = await initiateOtp({ email: targetEmail }).unwrap()

      setEmail(targetEmail)
      setCountdown(60)
      setCanResend(false)
      setOtpDigits(['', '', '', '', '', ''])
      verifyForm.reset({ otpCode: '' })
      setStep('verify')

      toast.success(res.message || 'Verification code sent to your email.', {
        id: 'forgot-password-initiate',
      })
    } catch (err: unknown) {
      const error = err as ApiErrorResponse
      const msg =
        error?.data?.error?.message ||
        error?.data?.message ||
        'Unable to send reset code. Please check your email and try again.'
      toast.error(msg, { id: 'forgot-password-initiate' })
    }
  }

  // Resend OTP handler
  const handleResendOtp = async () => {
    if (!canResend || isInitiating || !email) return
    try {
      toast.dismiss('forgot-password-resend')
      const res = await initiateOtp({ email }).unwrap()
      setCountdown(60)
      setCanResend(false)
      setOtpDigits(['', '', '', '', '', ''])
      verifyForm.reset({ otpCode: '' })
      toast.success(res.message || 'A new verification code has been sent.', {
        id: 'forgot-password-resend',
      })
    } catch (err: unknown) {
      const error = err as ApiErrorResponse
      const msg =
        error?.data?.error?.message ||
        error?.data?.message ||
        'Failed to resend code. Please try again.'
      toast.error(msg, { id: 'forgot-password-resend' })
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Step 2: Submit OTP Verification
  // ────────────────────────────────────────────────────────────────────────────
  const onVerifySubmit = async (values: VerifyOtpFormValues) => {
    try {
      toast.dismiss('forgot-password-verify')
      const code = sanitizeOtp(values.otpCode)
      const res = await verifyOtp({ email, otpCode: code }).unwrap()

      if (res.data?.resetToken) {
        setResetToken(res.data.resetToken)
        setStep('reset')
        toast.success(res.message || 'OTP verified successfully.', {
          id: 'forgot-password-verify',
        })
      } else {
        toast.error('Verification succeeded but reset token was missing.', {
          id: 'forgot-password-verify',
        })
      }
    } catch (err: unknown) {
      const error = err as ApiErrorResponse
      const msg =
        error?.data?.error?.message ||
        error?.data?.message ||
        'Invalid or expired verification code. Please try again.'
      toast.error(msg, { id: 'forgot-password-verify' })
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Step 3: Submit New Password
  // ────────────────────────────────────────────────────────────────────────────
  const onResetSubmit = async (values: ResetPasswordFormValues) => {
    try {
      toast.dismiss('forgot-password-reset')
      const res = await resetPassword({
        email,
        resetToken,
        newPassword: sanitizePassword(values.newPassword),
        confirmPassword: sanitizePassword(values.confirmPassword),
      }).unwrap()

      setStep('success')
      toast.success(res.message || 'Password reset successfully!', {
        id: 'forgot-password-reset',
      })
    } catch (err: unknown) {
      const error = err as ApiErrorResponse
      const msg =
        error?.data?.error?.message ||
        error?.data?.message ||
        'Failed to reset password. Please try again or request a new code.'
      toast.error(msg, { id: 'forgot-password-reset' })
    }
  }

  // Password criteria indicators helper
  const newPasswordValue = resetForm.watch('newPassword') || ''
  const hasMinLength = newPasswordValue.length >= 6
  const hasNumber = /\d/.test(newPasswordValue)
  const hasLetter = /[a-zA-Z]/.test(newPasswordValue)

  return (
    <div className="h-screen w-full bg-white flex flex-col lg:flex-row font-sans overflow-hidden select-none">
      {/* LEFT SIDE FORM CONTAINER */}
      <div className="w-full lg:w-5/12 flex flex-col justify-between p-6 sm:p-10 lg:p-12 xl:p-20 h-full bg-white z-20 overflow-y-auto">
        <div className="max-w-md w-full mx-auto my-auto py-3 sm:py-6 flex-1 flex flex-col justify-center">
          
          {/* Header: Logo */}
          <div className="flex flex-col items-start justify-start text-left mb-6 w-full">
            <div className="sm:-ml-[22px] -ml-[12px] -mb-[12px] flex justify-start items-center p-0 select-none w-full">
              <img
                className="w-full max-w-[120px] sm:max-w-[140px] h-auto object-contain object-left block m-0 p-0"
                src={Logo}
                alt="Dualon Logo"
              />
            </div>

            {/* Step Progress Pills */}
            {step !== 'success' && (
              <div className="flex items-center gap-2 mt-4 mb-2">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    step === 'initiate'
                      ? 'w-8 bg-brand-500'
                      : 'w-4 bg-brand-500/40'
                  }`}
                />
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    step === 'verify'
                      ? 'w-8 bg-brand-500'
                      : step === 'reset'
                      ? 'w-4 bg-brand-500/40'
                      : 'w-4 bg-gray-200'
                  }`}
                />
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    step === 'reset'
                      ? 'w-8 bg-brand-500'
                      : 'w-4 bg-gray-200'
                  }`}
                />
              </div>
            )}
          </div>

          {/* ────────────────────────────────────────────────────────────────── */}
          {/* STEP 1: INITIATE EMAIL FORM */}
          {/* ────────────────────────────────────────────────────────────────── */}
          {step === 'initiate' && (
            <div className="animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="text-left mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1.5 tracking-tight">
                  Forgot Password?
                </h1>
                <p className="text-gray-500 text-xs sm:text-sm font-normal leading-relaxed">
                  No worries! Enter your registered account email and we'll send a 6-digit verification code.
                </p>
              </div>

              <form
                className="space-y-5 text-left"
                onSubmit={initiateForm.handleSubmit(onInitiateSubmit)}
                noValidate
              >
                {/* Email Field */}
                <div className="space-y-1.5">
                  <label htmlFor="initiate-email" className="text-xs sm:text-sm font-semibold text-gray-900 block">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 pointer-events-none" />
                    <input
                      id="initiate-email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      placeholder="e.g. name@company.com"
                      {...initiateForm.register('email', {
                        onChange: (e) => {
                          e.target.value = sanitizeEmail(e.target.value)
                        },
                      })}
                      disabled={isInitiating}
                      aria-invalid={initiateForm.formState.errors.email ? 'true' : 'false'}
                      className={`w-full pl-10 pr-4 py-2.5 sm:py-3 rounded-xl border transition-colors text-xs text-gray-900 sm:text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-gray-50 disabled:text-gray-500 ${
                        initiateForm.formState.errors.email
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-200 focus:border-brand-500'
                      }`}
                    />
                  </div>
                  {initiateForm.formState.errors.email && (
                    <p className="text-red-500 text-[11px] sm:text-xs mt-1 font-medium" role="alert">
                      {initiateForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isInitiating}
                  className="w-full bg-brand-500 hover:bg-brand-600 text-white font-semibold py-2.5 sm:py-3 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                >
                  {isInitiating ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 text-white" />
                      <span className="text-xs sm:text-sm">Sending OTP Code...</span>
                    </>
                  ) : (
                    <span className="text-xs sm:text-sm">Send Verification Code</span>
                  )}
                </button>

                {/* Back to Login Link */}
                <div className="pt-2 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-brand-600 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Sign In</span>
                  </Link>
                </div>
              </form>
            </div>
          )}

          {/* ────────────────────────────────────────────────────────────────── */}
          {/* STEP 2: VERIFY OTP FORM */}
          {/* ────────────────────────────────────────────────────────────────── */}
          {step === 'verify' && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="text-left mb-6">
                <div className="inline-flex items-center justify-center p-2.5 bg-brand-50 rounded-xl text-brand-500 mb-3">
                  <KeyRound className="w-5 h-5" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1.5 tracking-tight">
                  Verify OTP Code
                </h1>
                <p className="text-gray-500 text-xs sm:text-sm font-normal leading-relaxed">
                  We've sent a 6-digit verification code to{' '}
                  <span className="font-semibold text-gray-900 break-all">{email}</span>.
                </p>
                <button
                  type="button"
                  onClick={() => setStep('initiate')}
                  className="mt-1 text-xs text-brand-500 hover:text-brand-600 hover:underline font-medium inline-block"
                >
                  Wrong email? Change
                </button>
              </div>

              <form
                className="space-y-6 text-left"
                onSubmit={verifyForm.handleSubmit(onVerifySubmit)}
                noValidate
              >
                {/* 6-box OTP Input */}
                <div className="space-y-2">
                  <label className="text-xs sm:text-sm font-semibold text-gray-900 block">
                    Enter 6-Digit Code <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center justify-between gap-2 sm:gap-3" onPaste={handleOtpPaste}>
                    {otpDigits.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => {
                          inputRefs.current[idx] = el
                        }}
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={1}
                        value={digit}
                        autoFocus={idx === 0}
                        disabled={isVerifying}
                        onChange={(e) => handleDigitChange(idx, e.target.value)}
                        onKeyDown={(e) => handleDigitKeyDown(idx, e)}
                        className={`w-11 h-12 sm:w-13 sm:h-14 text-center text-lg sm:text-xl font-bold rounded-xl border transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-gray-50 ${
                          digit
                            ? 'border-brand-500 bg-brand-50/20 text-gray-900'
                            : 'border-gray-200 bg-white text-gray-900 focus:border-brand-500'
                        }`}
                      />
                    ))}
                  </div>
                  {verifyForm.formState.errors.otpCode && (
                    <p className="text-red-500 text-[11px] sm:text-xs mt-1 font-medium" role="alert">
                      {verifyForm.formState.errors.otpCode.message}
                    </p>
                  )}
                </div>

                {/* Resend OTP Bar */}
                <div className="flex items-center justify-between text-xs sm:text-sm py-1">
                  <span className="text-gray-500">Didn't receive the code?</span>
                  {canResend ? (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isInitiating}
                      className="inline-flex items-center gap-1.5 font-semibold text-brand-500 hover:text-brand-600 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${isInitiating ? 'animate-spin' : ''}`} />
                      <span>Resend Code</span>
                    </button>
                  ) : (
                    <span className="font-medium text-gray-400">
                      Resend in <span className="font-semibold text-gray-600">{countdown}s</span>
                    </span>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isVerifying || otpDigits.join('').length !== 6}
                  className="w-full bg-brand-500 hover:bg-brand-600 text-white font-semibold py-2.5 sm:py-3 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 text-white" />
                      <span className="text-xs sm:text-sm">Verifying OTP...</span>
                    </>
                  ) : (
                    <span className="text-xs sm:text-sm">Verify & Proceed</span>
                  )}
                </button>

                {/* Back to Step 1 */}
                <div className="pt-1 text-center">
                  <button
                    type="button"
                    onClick={() => setStep('initiate')}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-brand-600 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Email Request</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ────────────────────────────────────────────────────────────────── */}
          {/* STEP 3: RESET PASSWORD FORM */}
          {/* ────────────────────────────────────────────────────────────────── */}
          {step === 'reset' && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="text-left mb-6">
                <div className="inline-flex items-center justify-center p-2.5 bg-brand-50 rounded-xl text-brand-500 mb-3">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1.5 tracking-tight">
                  Set New Password
                </h1>
                <p className="text-gray-500 text-xs sm:text-sm font-normal leading-relaxed">
                  Your identity has been verified. Create a strong password for your account.
                </p>
              </div>

              <form
                className="space-y-4 sm:space-y-5 text-left"
                onSubmit={resetForm.handleSubmit(onResetSubmit)}
                noValidate
              >
                {/* New Password Field */}
                <div className="space-y-1.5">
                  <label htmlFor="new-password" className="text-xs sm:text-sm font-semibold text-gray-900 block">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 pointer-events-none" />
                    <input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      autoFocus
                      {...resetForm.register('newPassword', {
                        onChange: (e) => {
                          e.target.value = sanitizePassword(e.target.value)
                        },
                      })}
                      disabled={isResetting}
                      aria-invalid={resetForm.formState.errors.newPassword ? 'true' : 'false'}
                      className={`w-full pl-10 pr-12 py-2.5 sm:py-3 rounded-xl border transition-colors text-xs text-gray-900 sm:text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-gray-50 disabled:text-gray-500 ${
                        resetForm.formState.errors.newPassword
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-200 focus:border-brand-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      disabled={isResetting}
                      className="absolute right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none disabled:pointer-events-none"
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {resetForm.formState.errors.newPassword && (
                    <p className="text-red-500 text-[11px] sm:text-xs mt-1 font-medium" role="alert">
                      {resetForm.formState.errors.newPassword.message}
                    </p>
                  )}
                </div>

                {/* Password Strength Checklist */}
                <div className="p-3 bg-gray-50 rounded-xl space-y-1.5 border border-gray-100">
                  <div className="flex items-center gap-2 text-[11px] sm:text-xs">
                    <div
                      className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                        hasMinLength ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <Check className="w-2.5 h-2.5" />
                    </div>
                    <span className={hasMinLength ? 'text-gray-800 font-medium' : 'text-gray-400'}>
                      At least 6 characters
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] sm:text-xs">
                    <div
                      className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                        hasLetter ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <Check className="w-2.5 h-2.5" />
                    </div>
                    <span className={hasLetter ? 'text-gray-800 font-medium' : 'text-gray-400'}>
                      Contains letters
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] sm:text-xs">
                    <div
                      className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
                        hasNumber ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <Check className="w-2.5 h-2.5" />
                    </div>
                    <span className={hasNumber ? 'text-gray-800 font-medium' : 'text-gray-400'}>
                      Contains at least one number
                    </span>
                  </div>
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-1.5">
                  <label htmlFor="confirm-password" className="text-xs sm:text-sm font-semibold text-gray-900 block">
                    Confirm New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 pointer-events-none" />
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter new password"
                      {...resetForm.register('confirmPassword', {
                        onChange: (e) => {
                          e.target.value = sanitizePassword(e.target.value)
                        },
                      })}
                      disabled={isResetting}
                      aria-invalid={resetForm.formState.errors.confirmPassword ? 'true' : 'false'}
                      className={`w-full pl-10 pr-12 py-2.5 sm:py-3 rounded-xl border transition-colors text-xs text-gray-900 sm:text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-gray-50 disabled:text-gray-500 ${
                        resetForm.formState.errors.confirmPassword
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-200 focus:border-brand-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isResetting}
                      className="absolute right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none disabled:pointer-events-none"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {resetForm.formState.errors.confirmPassword && (
                    <p className="text-red-500 text-[11px] sm:text-xs mt-1 font-medium" role="alert">
                      {resetForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isResetting}
                  className="w-full bg-brand-500 hover:bg-brand-600 text-white font-semibold py-2.5 sm:py-3 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand-500/20 mt-6"
                >
                  {isResetting ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 text-white" />
                      <span className="text-xs sm:text-sm">Resetting Password...</span>
                    </>
                  ) : (
                    <span className="text-xs sm:text-sm">Reset Password</span>
                  )}
                </button>

                {/* Back to Sign In Link */}
                <div className="pt-2 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-brand-600 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Sign In</span>
                  </Link>
                </div>
              </form>
            </div>
          )}

          {/* ────────────────────────────────────────────────────────────────── */}
          {/* STEP 4: SUCCESS CONFIRMATION */}
          {/* ────────────────────────────────────────────────────────────────── */}
          {step === 'success' && (
            <div className="text-center animate-in zoom-in-95 duration-300 py-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5 text-emerald-500 ring-8 ring-emerald-50/50 shadow-sm">
                <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-500" />
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 tracking-tight">
                Password Reset Complete!
              </h1>

              <p className="text-gray-500 text-xs sm:text-sm font-normal max-w-sm mx-auto mb-8 leading-relaxed">
                Your password has been successfully updated. You can now sign in to Dualon with your new credentials.
              </p>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full bg-brand-500 hover:bg-brand-600 text-white font-semibold py-2.5 sm:py-3 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              >
                <span className="text-xs sm:text-sm">Proceed to Sign In</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT SIDE VISUAL (Identical to Login) */}
      <div className="hidden lg:flex lg:w-7/12 relative bg-[#010519] flex-col justify-end overflow-hidden">
        <img
          src={loginImage}
          alt="Dualon Platform Visual"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-contain object-right-top pl-8 z-0 transition-transform duration-700 hover:scale-102"
        />
        <div className="relative z-10 p-16 max-w-2xl text-left bg-gradient-to-t from-[#010519] via-[#010519]/75 to-transparent">
          <h2 className="text-4xl font-semibold text-white tracking-tight leading-tight">
            Advanced Intelligence, <br /> Built for You.
          </h2>
          <p className="mt-4 text-base text-gray-400 max-w-xl leading-relaxed">
            Dualon is your unified hub for smarter decision-making. Leverage deep insight and a 360° view of your operations with a platform designed to simplify complexity and drive potential.
          </p>
        </div>
      </div>
    </div>
  )
}
